import { createSlice } from '@reduxjs/toolkit';
import { getUserInfo } from '../../utils/auth';

// 获取用户特定的 localStorage key
const getTabsStorageKey = () => {
  const userInfo = getUserInfo();
  const userId = userInfo?.userId || userInfo?.id || 'default';
  return `appTabs_${userId}`;
};

// 从 localStorage 获取初始标签页状态（基于用户ID）
const getInitialTabsState = () => {
  try {
    const storageKey = getTabsStorageKey();
    const savedTabs = localStorage.getItem(storageKey);
    if (savedTabs) {
      const parsedTabs = JSON.parse(savedTabs);
      // 如果有保存的标签页，过滤掉 dashboard 并清理
      if (parsedTabs.panes && parsedTabs.panes.length > 0) {
        // 过滤掉 dashboard 标签页
        const filteredPanes = parsedTabs.panes.filter(pane => pane.key !== 'dashboard');
        // 如果 activeKey 是 dashboard，设置为空或第一个标签页的 key
        let filteredActiveKey = parsedTabs.activeKey;
        if (filteredActiveKey === 'dashboard') {
          filteredActiveKey = filteredPanes.length > 0 ? filteredPanes[0].key : '';
        }
        return {
          activeKey: filteredActiveKey,
          panes: filteredPanes
        };
      }
    }
  } catch (error) {
    console.error('读取标签页状态失败:', error);
  }

  // 默认状态 - 空标签页
  return {
    activeKey: '',
    panes: []
  };
};

// 最大标签页数量
const MAX_TABS = 10;

const tabsSlice = createSlice({
  name: 'tabs',
  initialState: getInitialTabsState(),
  reducers: {
    // 添加新的标签页
    addTab: (state, action) => {
      const { key, title, path } = action.payload;
      
      // 阻止添加 dashboard 标签页
      if (key === 'dashboard') {
        return;
      }
      
      // 检查是否已经存在该标签页
      const existingTabIndex = state.panes.findIndex(pane => pane.key === key);
      
      if (existingTabIndex === -1) {
        // 如果不存在，检查是否超过最大数量限制
        if (state.panes.length >= MAX_TABS) {
          // 删除最旧的标签页（第一个）
          state.panes.shift();
        }
        
        // 添加新的标签页
        state.panes.push({
          key,
          title,
          path,
          closable: true
        });
      }
      
      // 设置为活跃标签
      state.activeKey = key;

      // 保存到 localStorage（使用用户特定的key）
      const storageKey = getTabsStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(state));
    },
    
    // 移除标签页
    removeTab: (state, action) => {
      const targetKey = action.payload;
      
      // 找到要删除的标签页索引
      const index = state.panes.findIndex(pane => pane.key === targetKey);
      
      // 移除标签页
      if (index !== -1) {
        state.panes.splice(index, 1);
      }
      
      // 如果删除的是当前活跃标签，则激活最后一个标签
      if (state.activeKey === targetKey) {
        state.activeKey = state.panes.length > 0 ? state.panes[state.panes.length - 1].key : '';
      }

      // 保存到 localStorage（使用用户特定的key）
      const storageKey = getTabsStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(state));
    },
    
    // 切换活跃标签
    setActiveTab: (state, action) => {
      state.activeKey = action.payload;

      // 保存到 localStorage（使用用户特定的key）
      const storageKey = getTabsStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(state));
    },

    // 重置标签页为默认状态
    resetTabs: (state) => {
      state.activeKey = '';
      state.panes = [];

      // 保存到 localStorage（使用用户特定的key）
      const storageKey = getTabsStorageKey();
      localStorage.setItem(storageKey, JSON.stringify(state));
    }
  }
});

export const { 
  addTab, 
  removeTab, 
  setActiveTab,
  resetTabs
} = tabsSlice.actions;

export default tabsSlice.reducer; 